/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@pinia/colada';
import AgentAccessService from '../services/AgentAccessService';
import type {
  AgentAccessAgent,
  AgentAccessConnection,
  AgentAccessPermission,
} from '../models/agentAccessTypes';

/**
 * @pinia/colada is an internal request/cache wrapper here — AgentAccess.vue and the dialogs never
 * import it. The store's own refs stay the public contract, and every read is a `load()`/action.
 *
 * agents and connections are `staleTime: 0` and driven via `.refetch()`, which ignores freshness:
 * an administrator reloading this screen after a withdrawal must see the firm's real state, never a
 * cached one. The permission catalogue is the opposite — it is a build-time constant on the server —
 * so it carries a real `staleTime` and is driven via `.refresh()`, which only fetches when stale.
 *
 * Both `.refetch()`/`.refresh()` calls pass `true` for `throwOnError`; the default swallows a failed
 * fetch and resolves with the entry's last-known state, which would hide a failure from `load()`'s
 * try/catch.
 *
 * Keys here are static, so two concurrent `load()` calls land on one cache entry and Colada aborts
 * the older. The query functions don't forward that signal, so the older call resolves with the
 * entry's *current* state rather than rejecting — possibly `{ data: undefined }`. Hence the token
 * guard, and why nothing below dereferences `state.data` without a fallback.
 */
const PERMISSIONS_STALE_TIME = 60 * 60 * 1000;

export const useAgentAccessStore = defineStore('agentAccess', () => {
  const agents = ref<AgentAccessAgent[]>([]);
  const connections = ref<AgentAccessConnection[]>([]);
  const permissions = ref<AgentAccessPermission[]>([]);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<unknown>(null);

  const approvedAgents = computed(() => agents.value.filter((a) => a.status === 'approved'));
  const connectionCount = computed(() => connections.value.length);
  const permissionGroups = computed(() => ({
    read: permissions.value.filter((p) => !p.isWrite && p.scope.startsWith('read:')),
    write: permissions.value.filter((p) => p.isWrite && !p.requiresExplicitGrant),
    send: permissions.value.filter((p) => p.requiresExplicitGrant),
    other: permissions.value.filter((p) => !p.isWrite && !p.scope.startsWith('read:')),
  }));

  const agentsQuery = useQuery({
    key: () => ['agent-access', 'agents'],
    query: () => AgentAccessService.fetchAgents(),
    enabled: false,
    staleTime: 0,
  });

  const connectionsQuery = useQuery({
    key: () => ['agent-access', 'connections'],
    query: () => AgentAccessService.fetchConnections(),
    enabled: false,
    staleTime: 0,
  });

  const permissionsQuery = useQuery({
    key: () => ['agent-access', 'permissions'],
    query: () => AgentAccessService.fetchPermissions(),
    enabled: false,
    staleTime: PERMISSIONS_STALE_TIME,
  });

  let loadToken = 0;

  async function load(silent = false): Promise<void> {
    const token = ++loadToken;
    loading.value = !silent;
    error.value = null;
    try {
      const [agentsState, connectionsState, permissionsState] = await Promise.all([
        agentsQuery.refetch(true),
        connectionsQuery.refetch(true),
        permissionsQuery.refresh(true),
      ]);
      if (token !== loadToken) return;
      agents.value = agentsState.data ?? [];
      connections.value = connectionsState.data ?? [];
      permissions.value = permissionsState.data ?? permissions.value;
    } catch (e) {
      if (token !== loadToken) return;
      error.value = e;
      throw e;
    } finally {
      if (token === loadToken) loading.value = false;
    }
  }

  const approveMutation = useMutation({
    mutation: ({ clientId, allowedScopes }: { clientId: string; allowedScopes: string[] | null }) =>
      AgentAccessService.approve(clientId, allowedScopes),
  });

  const withdrawMutation = useMutation({
    mutation: (clientId: string) => AgentAccessService.withdraw(clientId),
  });

  const restoreMutation = useMutation({
    mutation: (clientId: string) => AgentAccessService.restore(clientId),
  });

  const revokeMutation = useMutation({
    mutation: (grantId: string) => AgentAccessService.revokeConnection(grantId),
  });

  /**
   * Every mutation reloads both lists rather than patching one: withdrawing an agent ends its
   * connections and restoring brings them back, so the two are never independently correct.
   */
  async function runMutation(action: () => Promise<unknown>): Promise<void> {
    saving.value = true;
    error.value = null;
    try {
      await action();
      await load(true);
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      saving.value = false;
    }
  }

  const approve = (clientId: string, allowedScopes: string[] | null) =>
    runMutation(() => approveMutation.mutateAsync({ clientId, allowedScopes }));

  const withdraw = (clientId: string) => runMutation(() => withdrawMutation.mutateAsync(clientId));

  const restore = (clientId: string) => runMutation(() => restoreMutation.mutateAsync(clientId));

  const revokeConnection = (grantId: string) =>
    runMutation(() => revokeMutation.mutateAsync(grantId));

  return {
    agents,
    connections,
    permissions,
    loading,
    saving,
    error,
    approvedAgents,
    connectionCount,
    permissionGroups,
    load,
    approve,
    withdraw,
    restore,
    revokeConnection,
  };
});
